import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, Check, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.message) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSuccess(true);
      setFormData({ name: "", email: "", message: "" });
      toast.success("Message sent! We'll get back to you soon.");
    } catch (error) {
      console.error("Contact error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="py-6 px-6 md:px-12">
        <div className="max-w-5xl mx-auto">
          <Link 
            to="/" 
            data-testid="back-to-home"
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#F58227] transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Back home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title */}
            <div className="mb-12 text-center">
              <p className="text-sm font-medium uppercase tracking-widest text-[#0CBCBF] mb-4">
                Get in touch
              </p>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight text-[#27272A] mb-6">
                Say hello
              </h1>
              <p className="text-lg text-[#52525B] max-w-md mx-auto">
                Questions, feedback, or just want to chat? We'd love to hear from you.
              </p>
            </div>

            {/* Form */}
            {!isSuccess ? (
              <motion.form
                onSubmit={handleSubmit}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="space-y-6 bg-white rounded-3xl p-8 md:p-10 border border-stone-100 shadow-sm"
              >
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-[#27272A] mb-2">
                    Name <span className="text-[#A1A1AA]">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="What should we call you?"
                    data-testid="contact-name-input"
                    className="w-full h-12 bg-[#FDFBF7] border-2 border-stone-200 rounded-xl px-4 text-base focus:border-[#F58227] focus:ring-4 focus:ring-[#F58227]/10 transition-all outline-none placeholder:text-stone-400"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-[#27272A] mb-2">
                    Email <span className="text-[#F58227]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    required
                    data-testid="contact-email-input"
                    className="w-full h-12 bg-[#FDFBF7] border-2 border-stone-200 rounded-xl px-4 text-base focus:border-[#F58227] focus:ring-4 focus:ring-[#F58227]/10 transition-all outline-none placeholder:text-stone-400"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-[#27272A] mb-2">
                    Message <span className="text-[#F58227]">*</span>
                  </label>
                  <textarea
                    id="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    placeholder="What's on your mind?"
                    required
                    rows={5}
                    data-testid="contact-message-input"
                    className="w-full bg-[#FDFBF7] border-2 border-stone-200 rounded-xl px-4 py-3 text-base focus:border-[#F58227] focus:ring-4 focus:ring-[#F58227]/10 transition-all outline-none placeholder:text-stone-400 resize-none"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  data-testid="contact-submit-button"
                  className="w-full h-14 bg-[#F58227] text-white hover:bg-[#D96D19] rounded-full text-lg font-bold shadow-lg hover:shadow-[#F58227]/30 transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                >
                  {isLoading ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        className="w-5 h-5 border-2 border-white border-t-transparent rounded-full"
                      />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <span>Send Message</span>
                      <Send size={18} />
                    </>
                  )}
                </button>
              </motion.form>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
                data-testid="contact-success"
                className="bg-[#E0F7FA] rounded-3xl p-10 text-center"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#0CBCBF] flex items-center justify-center">
                  <Check size={32} className="text-white" />
                </div>
                <h2 className="text-2xl font-semibold text-[#27272A] mb-3">
                  Message received!
                </h2>
                <p className="text-[#52525B] mb-6">
                  Thanks for reaching out. We'll get back to you as soon as we can.
                </p>
                <button
                  onClick={() => setIsSuccess(false)}
                  className="text-[#0CBCBF] hover:text-[#F58227] font-medium transition-colors"
                >
                  Send another message
                </button>
              </motion.div>
            )}

            {/* Alternative contact */}
            <div className="mt-10 text-center">
              <div className="inline-flex items-center gap-3 text-[#A1A1AA]">
                <Mail size={18} />
                <span>Or email us directly at <a href="mailto:hello@wherewasi.app" className="text-[#0CBCBF] hover:text-[#F58227] transition-colors">hello@wherewasi.app</a></span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
